import { Good } from "@/entities/good";
import { GoodVersion } from "@/entities/goodVersion";
import { resolveImageUrl } from "@/utils/imageRef";
import { useAuthStore } from "@/stores/auth";

const WB_MEDIA_FILE_URL = "https://content-api.wildberries.ru/content/v3/media/file";

/**
 * Загрузка медиа на WB карточку.
 *
 * Раньше использовали /content/v3/media/save, который принимал массив URL'ов
 * и WB ходила за ними сама. Это ломалось локально (URL картинок указывал на
 * http://localhost:3001 — WB-серверам недоступен) и было крайне фрагильно в
 * целом (любой даунтайм нашей CDN — потеря картинок при публикации).
 *
 * Переехали на /content/v3/media/file: фронт сам скачивает картинку (любого
 * происхождения — локалбэк, прод-CDN, blob) и отправляет байты multipart'ом.
 * WB не нужно никуда ходить, картинки заливаются гарантированно.
 *
 * Загрузка ПОСЛЕДОВАТЕЛЬНАЯ — WB Content API не любит конкурентные media/file
 * на одну nmId (теряет порядок и иногда возвращает 409). Шлём по одной.
 */
export const wbUploadMedia = async (good: Good, version: GoodVersion): Promise<void> => {
  const nmId = good.publicationData?.wb?.nmId;
  if (!nmId) {
    throw new Error("wbUploadMedia: nmId не определён, карточка ещё не создана в WB");
  }
  const images = (version.media?.images ?? []).filter((ref): ref is string => Boolean(ref?.trim()));
  if (images.length === 0) return;

  const authStore = useAuthStore();
  const wbToken = authStore.marketplaceConnections.wildberries?.token;
  if (!wbToken) {
    throw new Error("wbUploadMedia: WB token не найден в auth store");
  }

  for (let i = 0; i < images.length; i++) {
    const photoNumber = i + 1;
    const sourceUrl = resolveImageUrl(images[i]);
    const file = await fetchImageAsFile(sourceUrl, `photo-${photoNumber}.jpg`);

    const formData = new FormData();
    formData.append("uploadfile", file, file.name);

    const response = await fetch(WB_MEDIA_FILE_URL, {
      method: "POST",
      headers: {
        // НЕ ставим Content-Type — браузер сам добавит multipart boundary.
        Authorization: `Bearer ${wbToken}`,
        "X-Nm-Id": String(nmId),
        "X-Photo-Number": String(photoNumber),
      },
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(
        `wbUploadMedia: WB media/file failed (nmId=${nmId}, photo=${photoNumber}, status=${response.status}): ${text.slice(0, 300)}`,
      );
    }
  }
};

/**
 * Скачать картинку и обернуть в File для multipart-аплоада.
 * MIME type берём из Content-Type ответа; имя файла подсказывает WB расширение.
 */
const fetchImageAsFile = async (url: string, fileName: string): Promise<File> => {
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`wbUploadMedia: failed to fetch ${url}: ${response.status}`);
  }
  const blob = await response.blob();
  const mime = blob.type || "image/jpeg";
  return new File([blob], fileName, { type: mime });
};
